import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import {
  Grid,
  Typography,
  Box,
  IconButton,
  Paper,
  CircularProgress,
  Tooltip,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Button,
  StepContent,
  Divider,
} from "@mui/material";
import { Lock, Refresh, Info as InfoIcon } from "@mui/icons-material";
import { FormField } from "../inputs/react-hook-form/FormField";
import { FlexBox, GridItem, StyledSaveButton } from "../components/ui";
import { useBreakpoints } from "../hooks/useBreakpoints";
import get from "lodash.get";
import { useApp } from "../../AppProvider";
import { useReactToPrint } from "react-to-print";

export const SectionContainer = (props) => (
  <Paper sx={{ width: "100%", padding: 4 }} {...props} />
);

export const Header = (props) => (
  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }} {...props} />
);

const SectionHeader = ({ title, subtitle }) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      padding: 2,
      mb: 3,
      backgroundColor: (theme) => theme.palette.background.default,
    }}
  >
    <Typography variant="h4" sx={{ fontWeight: "bold", mb: subtitle ? 1 : 0 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="subtitle1"
        sx={{ color: "text.secondary", fontStyle: "italic" }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StyledChip = (props) => (
  <Chip
    {...props}
    sx={{
      whiteSpace: "nowrap",
      height: 20,
      fontSize: "0.7rem",
      background: (theme) => theme.palette.background.default,
      ".MuiChip-label": {
        padding: "0 6px",
      },
      ...props.sx,
    }}
  />
);

const HorizontalStepperContainer = (props) => (
  <Box sx={{ width: "100%" }} {...props} />
);

const StyledHorizontalStepper = (props) => (
  <Stepper {...props} sx={{ width: "100%", overflow: "auto", py: 1 }} />
);

const StyledStep = ({ steps, ...props }) => (
  <Step {...props} sx={{ minWidth: `${Math.min(120, 960 / (steps - 1))}px` }} />
);

const Label = ({ isDirty, hasError, children }) => (
  <Typography
    sx={(theme) => ({
      fontWeight: 600,
      borderLeft: `4px solid ${
        isDirty
          ? hasError
            ? theme.palette.error.main
            : theme.palette.secondary.main
          : "transparent"
      }`,
      pl: 0.5,
    })}
    className="no-border-on-print"
    color={hasError ? "error" : "textPrimary"}
  >
    {children}
  </Typography>
);

const ResetButton = ({ onClick }) => (
  <Tooltip title="Discard changes" arrow>
    <IconButton onClick={onClick} size="small" className="no-print">
      <Refresh />
    </IconButton>
  </Tooltip>
);

const FieldLabel = ({ field, isDirty, hasError, infoTooltip }) => (
  <Box display="flex" alignItems="center" style={{ gap: "8px" }}>
    <Label
      color={hasError ? "error" : "textPrimary"}
      isDirty={isDirty}
      hasError={hasError}
    >
      <Tooltip title={isDirty ? "Has unsaved changes" : ""}>
        <span>{field.label}</span>
      </Tooltip>
    </Label>
    {infoTooltip && (
      <Tooltip title={infoTooltip} arrow>
        <InfoIcon fontSize="small" color="secondary" className="no-print" />
      </Tooltip>
    )}
  </Box>
);

const FormFieldWrapper = ({ field, formMethods, lookupOptions }) => {
  const { control, formState, resetField, setValue } = formMethods;
  const isDirty = !!get(formState.dirtyFields, field.name);
  const hasError = !!get(formState.errors, field.name);
  const isReadOnly = field?.readOnly;
  const infoTooltip = field?.infoTooltip;

  // Handle showIf logic
  const { fn: showIfFn, deps: showIfDeps } = field.showIf || {};
  const showIfValuesArray = useWatch({ control, name: showIfDeps || [] });
  const showIfValues = (showIfDeps || []).reduce((acc, key, index) => {
    acc[key] = showIfValuesArray[index] ?? null;
    return acc;
  }, {});
  const shouldShow = showIfFn ? showIfFn(showIfValues) : true;

  const prevShouldShow = useRef(shouldShow);
  useEffect(() => {
    if (prevShouldShow.current && !shouldShow) {
      setValue(field.name, field.showIf?.resetValue ?? null);
    }
    prevShouldShow.current = shouldShow;
  }, [shouldShow, field.name, setValue, field.showIf?.resetValue]);

  const isRequired =
    (field.validation && !field.validation.safeParse(undefined).success) ||
    (shouldShow && field.showIf?.required);

  if (!shouldShow) return null;

  // Handle map separately
  if (field.type === "map") {
    return (
      <Grid item xs={12} className="no-print">
        <FormField field={field} />
      </Grid>
    );
  }

  // Handle subHeader type separately
  if (field.type === "subHeader") {
    if (!shouldShow) return null;

    // Get all form values for dynamic content
    const allValues = formMethods.watch();

    // Handle dynamic label (can be a function or static string)
    const label =
      typeof field.label === "function" ? field.label(allValues) : field.label;

    // Handle dynamic content (can be a function, React element, or static string)
    const content =
      typeof field.content === "function"
        ? field.content(allValues)
        : field.content;

    return (
      <Grid item xs={12}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            color: "secondary.main",
            mt: 2,
            mb: 1,
          }}
        >
          {label}
        </Typography>
        {content && (
          <Typography component="div" variant="body2" sx={{ mb: 2 }}>
            {content}
          </Typography>
        )}
        <Divider sx={{ mb: 2 }} />
      </Grid>
    );
  }

  if (field.type === "custom" && typeof field.render === "function") {
    if (!shouldShow) return null;
    return (
      <Grid item xs={12}>
        {field.render()}
      </Grid>
    );
  }

  return (
    <GridItem {...field?.GridItem}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb="8px"
      >
        <FieldLabel
          field={field}
          isDirty={isDirty}
          hasError={hasError}
          infoTooltip={infoTooltip}
        />
        {isRequired && <StyledChip label="Required" className="no-print" />}
      </Box>
      <Box display="flex" alignItems="center" style={{ gap: "4px" }}>
        <FormField field={field} lookupOptions={lookupOptions} />
        {!isReadOnly && isDirty && (
          <ResetButton onClick={() => resetField(field.name)} />
        )}
        {isReadOnly && (
          <Tooltip title="This field cannot be edited" arrow>
            <Lock color="disabled" className="no-print" />
          </Tooltip>
        )}
      </Box>
    </GridItem>
  );
};

export const FormSection = ({ section, formMethods, lookupsData, isAdmin }) => (
  <SectionContainer key={section.title}>
    <SectionHeader title={section.title} subtitle={section.subtitle} />

    <Grid container spacing={4}>
      {section.fields
        .filter((field) => !field?.adminOnly || isAdmin)
        .map((field) => (
          <FormFieldWrapper
            key={field.name}
            field={field}
            formMethods={formMethods}
            lookupOptions={getLookupOptions(field, lookupsData)}
          />
        ))}
    </Grid>
  </SectionContainer>
);

const VerticalFormStepper = ({
  stepNames,
  currentStepIndex,
  setCurrentStepIndex,
  children,
}) => {
  const stepRefs = useRef([]);

  useEffect(() => {
    const firstStep = stepRefs.current[0]; // First step for reference
    const currentStep = stepRefs.current[currentStepIndex];

    if (firstStep && currentStep) {
      const firstStepTop =
        firstStep.getBoundingClientRect().top + window.scrollY;
      const currentStepTop =
        currentStep.getBoundingClientRect().top + window.scrollY;

      const offset = currentStepTop - firstStepTop;

      window.scrollTo({
        top: offset,
        behavior: "smooth",
      });
    }
  }, [currentStepIndex]);

  return (
    <Stepper
      orientation="vertical"
      activeStep={currentStepIndex}
      nonLinear
      style={{ padding: "8px" }}
    >
      {stepNames.map((step, index) => (
        <Step key={index} completed={index < currentStepIndex}>
          <StepLabel
            onClick={() => setCurrentStepIndex(index)}
            style={{ cursor: "pointer" }}
          >
            <div ref={(el) => (stepRefs.current[index] = el)}>{step}</div>
          </StepLabel>
          {index === currentStepIndex && (
            <StepContent style={{ paddingLeft: "4px", paddingRight: 0 }}>
              {children}
            </StepContent>
          )}
        </Step>
      ))}
    </Stepper>
  );
};

const HorizontalFormStepper = ({
  stepNames,
  currentStepIndex,
  setCurrentStepIndex,
}) => {
  return (
    <HorizontalStepperContainer>
      <StyledHorizontalStepper
        activeStep={currentStepIndex}
        alternativeLabel
        nonLinear
      >
        {stepNames.map((step, index) => (
          <StyledStep
            steps={stepNames.length}
            key={index}
            completed={index < currentStepIndex}
          >
            <StepLabel
              style={{ cursor: "pointer" }}
              onClick={() => setCurrentStepIndex(index)}
            >
              {step}
            </StepLabel>
          </StyledStep>
        ))}
      </StyledHorizontalStepper>
    </HorizontalStepperContainer>
  );
};

const getLookupOptions = (field, lookupsData) =>
  field?.lookupTable?.tableName && lookupsData[field.lookupTable.tableName]
    ? lookupsData[field.lookupTable.tableName]
    : [];

const processSteps = (config, isAdmin) => {
  let lastStep = null;
  return config
    .filter((section) => !section.adminOnly || isAdmin)
    .map((section) => {
      if (section.step) lastStep = section.step;
      return { ...section, step: lastStep };
    });
};

export function DynamicForm({ config, formMethods, onSubmit, lookupsData }) {
  const [showAll, setShowAll] = useState(false);
  const formRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => formRef.current,
    onAfterPrint: () => {
      setTimeout(() => setShowAll(false), 100); // Delay helps avoid flicker
    },
  });

  const handleClickPrint = () => {
    setShowAll(true);
    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  const {
    formState: { isDirty, isSubmitting },
  } = formMethods;
  const { currentUser, doToast } = useApp();
  const { isDownSm, isXs } = useBreakpoints();

  const isAdmin = currentUser?.isAdmin;
  const gap = isDownSm ? "12px" : "24px";
  const buttonSize = isXs ? "medium" : "large";

  const processedConfig = processSteps(config, isAdmin);

  const stepGroups = processedConfig.reduce((acc, section) => {
    acc[section.step] = acc[section.step] || [];
    acc[section.step].push(section);
    return acc;
  }, {});

  const stepNames = Object.keys(stepGroups);
  const hasSteps = stepNames.length > 1;
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const currentStepName = stepNames[currentStepIndex];

  const handleNext = () => {
    setCurrentStepIndex((prev) => prev + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setCurrentStepIndex((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onInvalid = (errors) => {
    const errorCount = Object.keys(errors).length;

    const fieldLabelMap = config.reduce((acc, section) => {
      section.fields.forEach((field) => {
        acc[field.name] = field.label;
      });
      return acc;
    }, {});

    const errorMessages = Object.entries(errors).map(([fieldName, error]) => {
      const label = fieldLabelMap[fieldName] || fieldName;
      return `${label}: ${error.message}`;
    });

    const toastMessage = (
      <div>
        Please correct {errorCount} error(s) before submitting:
        <br />
        {errorMessages.map((message, index) => (
          <div key={index}>
            {index + 1}. {message}
          </div>
        ))}
      </div>
    );

    doToast("error", toastMessage, { persist: true });
    console.error("Validation errors:", errors);
  };

  // If there's only one step, no stepper
  if (!hasSteps || showAll) {
    return (
      <FormProvider {...formMethods}>
        <form style={{ width: "100%" }}>
          <FlexBox style={{ gap }}>
            <div ref={formRef}>
              {processedConfig.map(
                (section) =>
                  (!section?.adminOnly || isAdmin) && (
                    <Box
                      height="100%"
                      width="100%"
                      key={section.title}
                      className="form-section"
                    >
                      <FormSection
                        section={section}
                        formMethods={formMethods}
                        lookupsData={lookupsData}
                        isAdmin={isAdmin}
                      />
                    </Box>
                  )
              )}
            </div>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={4}
              gap={2}
            >
              <StyledSaveButton
                onClick={formMethods.handleSubmit(onSubmit, onInvalid)}
                variant="contained"
                color="secondary"
                size={buttonSize}
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? <CircularProgress size={24} /> : "Submit"}
              </StyledSaveButton>

              <Button
                variant="outlined"
                onClick={handleClickPrint}
                size={buttonSize}
              >
                Print / Save as PDF
              </Button>
            </Box>
          </FlexBox>
        </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form style={{ width: "100%" }}>
        {isDownSm ? (
          // SMALL SCREENS => VERTICAL
          <VerticalFormStepper
            stepNames={stepNames}
            currentStepIndex={currentStepIndex}
            setCurrentStepIndex={setCurrentStepIndex}
          >
            <FlexBox style={{ gap }}>
              {stepGroups[currentStepName]?.map(
                (section) =>
                  (!section?.adminOnly || isAdmin) && (
                    <Box height="100%" width="100%" key={section.title}>
                      <FormSection
                        section={section}
                        formMethods={formMethods}
                        lookupsData={lookupsData}
                        isAdmin={isAdmin}
                      />
                    </Box>
                  )
              )}

              <Box
                display="flex"
                alignSelf="start"
                style={{ gap: "8px", width: "100%" }}
              >
                <Button disabled={currentStepIndex === 0} onClick={handleBack}>
                  Back
                </Button>

                <StyledSaveButton
                  onClick={
                    currentStepIndex < stepNames.length - 1
                      ? handleNext
                      : formMethods.handleSubmit(onSubmit, onInvalid)
                  }
                  style={{ width: "fit-content" }}
                  variant="contained"
                  color="secondary"
                  size={buttonSize}
                  disabled={
                    isSubmitting ||
                    (currentStepIndex === stepNames.length - 1 && !isDirty)
                  }
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} />
                  ) : currentStepIndex < stepNames.length - 1 ? (
                    "Next"
                  ) : (
                    "Submit"
                  )}
                </StyledSaveButton>
                {currentStepIndex === stepNames.length - 1 && (
                  <Button
                    sx={{ marginLeft: "auto" }}
                    variant="outlined"
                    onClick={handleClickPrint}
                    size={buttonSize}
                  >
                    Print / PDF
                  </Button>
                )}
              </Box>
            </FlexBox>
          </VerticalFormStepper>
        ) : (
          // MEDIUM + SCREENS => HORIZONTAL
          <FlexBox style={{ gap }}>
            <HorizontalFormStepper
              stepNames={stepNames}
              currentStepIndex={currentStepIndex}
              setCurrentStepIndex={setCurrentStepIndex}
            />
            {stepGroups[currentStepName]?.map(
              (section) =>
                (!section?.adminOnly || isAdmin) && (
                  <Box height="100%" width="100%" key={section.title}>
                    <FormSection
                      section={section}
                      formMethods={formMethods}
                      lookupsData={lookupsData}
                      isAdmin={isAdmin}
                    />
                  </Box>
                )
            )}

            <Button disabled={currentStepIndex === 0} onClick={handleBack}>
              Back
            </Button>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={4}
              gap={2}
            >
              <StyledSaveButton
                onClick={
                  currentStepIndex < stepNames.length - 1
                    ? handleNext
                    : formMethods.handleSubmit(onSubmit, onInvalid)
                }
                variant="contained"
                color="secondary"
                size={buttonSize}
                disabled={
                  isSubmitting ||
                  (currentStepIndex === stepNames.length - 1 && !isDirty)
                }
              >
                {isSubmitting ? (
                  <CircularProgress size={24} />
                ) : currentStepIndex < stepNames.length - 1 ? (
                  "Next"
                ) : (
                  "Submit"
                )}
              </StyledSaveButton>
              {currentStepIndex === stepNames.length - 1 && (
                <Button
                  sx={{ marginLeft: "auto" }}
                  variant="outlined"
                  onClick={handleClickPrint}
                  size={buttonSize}
                >
                  Print / Save as PDF
                </Button>
              )}
            </Box>
          </FlexBox>
        )}
      </form>
    </FormProvider>
  );
}
